/**
 * Files for a reply, forward or new email: pick or drop them, they upload to ONE at once (big ones in pieces), and the
 * email carries their ids. Sending waits until every file is up.
 */
import { Paperclip, X } from "lucide-react";
import { useRef, useState } from "react";
import { upload } from "./TaskFiles";

export interface MailFile { key: string; name: string; size: number; progress: number; id?: string; error?: string }
const human = (n: number) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const MAX = 150 * 1024 * 1024;

export function useMailFiles() {
  const [files, setFiles] = useState<MailFile[]>([]);
  const patch = (key: string, p: Partial<MailFile>) => setFiles((cur) => cur.map((f) => (f.key === key ? { ...f, ...p } : f)));
  const add = (list: FileList | File[] | null) => {
    for (const f of Array.from(list ?? [])) {
      const key = `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`;
      setFiles((cur) => {
        const total = cur.reduce((n, x) => n + x.size, 0) + f.size;
        return [...cur, { key, name: f.name, size: f.size, progress: 0, ...(total > MAX ? { error: "Over 150 MB together" } : {}) }];
      });
      void (async () => {
        try {
          const r = await upload(f, (p) => patch(key, { progress: p }));
          patch(key, { id: r.id, progress: 1 });
        } catch (e) { patch(key, { error: (e as Error).message }); }
      })();
    }
  };
  const remove = (key: string) => setFiles((cur) => cur.filter((f) => f.key !== key));
  /** A file already in ONE (your files, or copied from FusionDocs). */
  const addDoc = (d: { id: string; name: string; size: number }) => setFiles((cur) => (cur.some((f) => f.id === d.id) ? cur : [...cur, { key: `doc-${d.id}`, name: d.name, size: d.size, progress: 1, id: d.id }]));
  const ids = files.filter((f) => f.id && !f.error).map((f) => f.id!);
  const busy = files.some((f) => !f.id && !f.error);
  const broken = files.some((f) => f.error);
  /** Drop files anywhere on the composer. */
  const dropProps = {
    onDragOver: (e: React.DragEvent) => { if (e.dataTransfer.types.includes("Files")) e.preventDefault(); },
    onDrop: (e: React.DragEvent) => { if (e.dataTransfer.files.length) { e.preventDefault(); add(e.dataTransfer.files); } },
  };
  return { files, add, addDoc, remove, ids, busy, broken, dropProps };
}

export function MailFiles({ state, compact }: { state: ReturnType<typeof useMailFiles>; compact?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={input} type="file" multiple hidden onChange={(e) => { state.add(e.target.files); e.target.value = ""; }} />
      <button type="button" className={compact ? "btn !min-h-0 !py-1.5 text-xs" : "btn"} onClick={() => input.current?.click()} title="Attach files (or drop them here)"><Paperclip size={13} /> Attach</button>
      {state.files.length > 0 && (
        <span className="flex w-full flex-wrap gap-1.5">
          {state.files.map((f) => (
            <span key={f.key} className={`chip max-w-full gap-1.5 text-xs ${f.error ? "border-red-300 text-red-700" : ""}`} title={f.error ?? f.name}>
              <Paperclip size={11} className="shrink-0" />
              <span className="truncate">{f.name}</span>
              <span className="muted shrink-0">{f.error ? "failed" : f.id ? human(f.size) : `${Math.round(f.progress * 100)}%`}</span>
              <button type="button" className="shrink-0" onClick={() => state.remove(f.key)} aria-label={`Remove ${f.name}`}><X size={11} /></button>
            </span>
          ))}
        </span>
      )}
    </>
  );
}
